/**
 * Login Screen 1: Email/Phone Input
 *
 * First screen of Cognito login flow:
 * 1. User enters email or phone number
 * 2. Click "Continue"
 * 3. onSubmit callback handles check-user logic
 */

import React, { useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

interface LoginScreen1Props {
  onSubmit: (identifier: string) => Promise<void>;
  error: string | null;
}

export function LoginScreen1({ onSubmit, error }: LoginScreen1Props) {
  const [identifier, setIdentifier] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await onSubmit(identifier);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error">{error}</Alert>}

      <form onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <TextField
            fullWidth
            label="Email or Phone Number"
            variant="outlined"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            placeholder="you@example.com or +1234567890"
            disabled={loading}
            autoFocus
            required
            helperText="We'll use this to find your account and send you a sign-in code if needed."
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={loading || !identifier}
          >
            {loading ? (
              <>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                Checking...
              </>
            ) : (
              'Continue'
            )}
          </Button>
        </Stack>
      </form>

      <Typography variant="caption" color="text.secondary" align="center">
        By continuing, you agree to our Terms of Service and Privacy Policy
      </Typography>
    </Stack>
  );
}
